/**
 * Structured editor for a step's trigger `condition`.
 *
 * Two shapes are editable visually, both of which round-trip through the exact same
 * `condition` JSON lib/conditions.js already evaluates (no engine change):
 *
 *   1. a single field condition — "only apply this step when the matter is in practice
 *      group X", "…when it touches area of law Y", "…when it is a new client"
 *   2. an OR of AND groups — one or more "rule groups", each group a list of field
 *      conditions AND-ed together, the groups themselves OR-ed. This is the shape the
 *      General Counsel gate uses (new client AND private-sector group) OR (existing
 *      client AND litigation AND private-sector group), and the shape future
 *      multi-condition gates will want.
 *
 * Deliberately two levels only (AND within OR): no arbitrary nesting, no `not` around a
 * whole group. Anything the model cannot represent falls back to the raw JSON textarea
 * exactly as before, so no existing condition is ever destroyed by opening this editor.
 *
 * All parsing/serializing/editing logic lives in lib/conditionBuilderModel.js, which is
 * unit-tested directly (web/'s test runner is plain `node --test`, no JSX transform).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BUILDER_FIELDS,
  addCondition,
  addGroup,
  fieldByPath,
  isConditionComplete,
  opLabel,
  opsFor,
  parseCondition,
  removeCondition,
  removeGroup,
  serializeGroups,
  setConditionField,
  setConditionOp,
  setConditionValues,
  toggleConditionValue,
} from '../lib/conditionBuilderModel';

const field = 'w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm';
const sameJson = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export default function ConditionBuilder({ value, onChange, practiceGroups, areasOfLaw, fields: suppliedFields }) {
  // Approval-flow callers omit `fields`, preserving their established curated field
  // picker. Field-visibility callers provide the live, layout-derived field list.
  const fields = suppliedFields?.length ? suppliedFields : BUILDER_FIELDS;
  const parsed = useMemo(() => parseCondition(value, fields), [value, fields]);
  const [mode, setMode] = useState(() => (parsed ? 'builder' : 'raw'));
  const [groups, setGroups] = useState(() => (parsed ? parsed.groups : [{ conditions: [] }]));

  // Blank, not-yet-filled-in rows are intentionally NOT serialized, so the builder keeps
  // its own working copy rather than re-deriving it from `value` on every keystroke —
  // otherwise adding a row would immediately erase it again. This ref tracks what we last
  // emitted so a change that came from somewhere else (the raw editor, "Discard changes")
  // is still picked up.
  const lastEmitted = useRef(serializeGroups(parsed ? parsed.groups : [], fields));

  useEffect(() => {
    if (sameJson(value ?? true, lastEmitted.current)) return;
    const next = parseCondition(value, fields);
    if (!next) return; // unsupported shape: the raw editor owns it
    setGroups(next.groups);
    lastEmitted.current = serializeGroups(next.groups, fields);
  }, [value, fields]);

  const apply = (nextGroups) => {
    setGroups(nextGroups);
    const serialized = serializeGroups(nextGroups, fields);
    lastEmitted.current = serialized;
    onChange(serialized);
  };

  const optionsFor = (fieldDef) => {
    if (!fieldDef) return [];
    if (fieldDef.options) return fieldDef.options;
    if (!fieldDef.optionsFrom) return [];
    return (fieldDef.optionsFrom === 'practiceGroups' ? practiceGroups : areasOfLaw) || [];
  };

  const completeRows = groups.reduce(
    (n, g) => n + (g.conditions || []).filter((condition) => isConditionComplete(condition, fields)).length,
    0
  );
  const filledGroups = groups.filter((g) =>
    (g.conditions || []).some((condition) => isConditionComplete(condition, fields))
  ).length;

  return (
    <div>
      <div className="mb-2 flex gap-3 text-xs">
        <button
          type="button"
          onClick={() => setMode('builder')}
          disabled={!parsed && mode === 'raw'}
          title={
            !parsed && mode === 'raw'
              ? 'This condition is more complex than the builder can show. Edit it as JSON.'
              : undefined
          }
          className={
            mode === 'builder'
              ? 'font-semibold text-firm-700 dark:text-firm-100'
              : 'text-slate-500 dark:text-slate-400 hover:text-firm-700 dark:hover:text-firm-100 disabled:cursor-not-allowed disabled:text-slate-300 dark:disabled:text-slate-500'
          }
        >
          Builder
        </button>
        <button
          type="button"
          onClick={() => setMode('raw')}
          className={mode === 'raw' ? 'font-semibold text-firm-700 dark:text-firm-100' : 'text-slate-500 dark:text-slate-400 hover:text-firm-700 dark:hover:text-firm-100'}
        >
          Advanced (JSON)
        </button>
      </div>

      {mode === 'builder' ? (
        <div className="space-y-2">
          {groups.map((group, gi) => (
            <div key={gi}>
              {gi > 0 && (
                <div className="my-1 flex items-center gap-2">
                  <span className="rounded bg-amber-100 dark:bg-amber-900 px-2 py-0.5 text-xs font-semibold text-amber-900 dark:text-amber-100">
                    OR
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">…or instead, when all of:</span>
                </div>
              )}
              <div className="space-y-2 rounded border border-slate-200 dark:border-slate-700 p-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-600 dark:text-slate-300">
                    {gi === 0
                      ? 'Apply this step when ALL of these are true:'
                      : `Rule group ${gi + 1} — when ALL of these are true:`}
                  </span>
                  {groups.length > 1 && (
                    <button
                      type="button"
                      onClick={() => apply(removeGroup(groups, gi))}
                      className="text-xs text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400"
                    >
                      Remove group
                    </button>
                  )}
                </div>

                {(group.conditions || []).length === 0 && (
                  <p className="text-xs text-slate-400 dark:text-slate-500">
                    No conditions — this step always applies.
                  </p>
                )}

                {(group.conditions || []).map((cond, ci) => {
                  const fieldDef = fieldByPath(cond.path, fields) || fields[0];
                  const options = optionsFor(fieldDef);
                  return (
                    <div key={ci} className="rounded bg-slate-50 dark:bg-slate-800 p-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {ci > 0 && (
                          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">AND</span>
                        )}
                        <select
                          className={`${field} w-auto`}
                          aria-label="Field"
                          value={fieldDef.path}
                          onChange={(e) => apply(setConditionField(groups, gi, ci, e.target.value, fields))}
                        >
                          {fields.map((f) => (
                            <option key={f.path} value={f.path}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                        <select
                          className={`${field} w-auto`}
                          aria-label="Operator"
                          value={cond.op}
                          onChange={(e) => apply(setConditionOp(groups, gi, ci, e.target.value))}
                        >
                          {opsFor(fieldDef).map(([op]) => (
                            <option key={op} value={op}>
                              {opLabel(fieldDef, op)}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => apply(removeCondition(groups, gi, ci))}
                          className="ml-auto text-xs text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400"
                          title="Remove this condition"
                        >
                          Remove
                        </button>
                      </div>

                      {fieldDef.kind !== 'boolean' &&
                        cond.op !== 'exists' &&
                        cond.op !== 'not_exists' &&
                        (options.length === 0 ? (
                          <label className="mt-2 block text-xs text-slate-500 dark:text-slate-400">
                            Value
                            <input
                              className={`${field} mt-1`}
                              value={cond.values[0] ?? ''}
                              onChange={(event) =>
                                apply(
                                  setConditionValues(
                                    groups,
                                    gi,
                                    ci,
                                    event.target.value === '' ? [] : [event.target.value]
                                  )
                                )
                              }
                              placeholder={`Enter ${fieldDef.label.toLowerCase()} value`}
                            />
                          </label>
                        ) : (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {options.map((o) => (
                              <label
                                key={o.id}
                                className={`flex items-center gap-1 rounded border px-2 py-1 text-xs ${
                                  cond.values.includes(o.id)
                                    ? 'border-firm-700 dark:border-firm-500 bg-firm-50 dark:bg-firm-900 text-firm-800 dark:text-firm-100'
                                    : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  className="hidden"
                                  checked={cond.values.includes(o.id)}
                                  onChange={() => apply(toggleConditionValue(groups, gi, ci, o.id))}
                                />
                                {o.name || o.label}
                              </label>
                            ))}
                          </div>
                        ))}
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={() => apply(addCondition(groups, gi, fields))}
                  className="text-xs font-medium text-firm-700 dark:text-firm-100 hover:text-firm-900 dark:hover:text-white"
                >
                  + Add condition (AND)
                </button>
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => apply(addGroup(groups, fields))}
              className="text-xs font-medium text-firm-700 dark:text-firm-100 hover:text-firm-900 dark:hover:text-white"
            >
              + Add alternative rule group (OR)
            </button>
            <span className="text-xs text-slate-400 dark:text-slate-500">
              {completeRows === 0
                ? 'No conditions — this step always applies (same as leaving it unconditional).'
                : filledGroups > 1
                  ? `${filledGroups} alternative rule groups; the step applies if any group matches.`
                  : `${completeRows} condition${completeRows === 1 ? '' : 's'}, all of which must match.`}
            </span>
          </div>
        </div>
      ) : (
        <RawConditionEditor value={value} onChange={onChange} />
      )}
    </div>
  );
}

function RawConditionEditor({ value, onChange }) {
  const [text, setText] = useState(() => JSON.stringify(value ?? true, null, 2));
  const [error, setError] = useState('');

  const commit = () => {
    try {
      onChange(JSON.parse(text));
      setError('');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      <textarea
        rows={4}
        className={`${field} mt-1 font-mono text-xs`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
      />
      {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
    </>
  );
}
